import { BID_LIMIT_SLICE_CACHE_TIME } from './bidLimit.types';
import { createSelector } from '@reduxjs/toolkit';
import { getAuthToken, getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { GlobalState } from '@/redux/store';
import { isEmpty } from '@/utils/isEmpty/isEmpty';

const idSelector = (_: GlobalState, id: number) => id;

const bidLimitSelector = createSelector(
    (state: GlobalState) => state,
    (state) => state.bidLimit
);

const byIdSelector = createSelector(bidLimitSelector, (state) => state.byId);

const loadedSelector = createSelector(bidLimitSelector, (state) => state.loaded);

const loadingSelector = createSelector(bidLimitSelector, (state) => state.loading);

export const getIsBidLimitLoaded = createSelector([loadedSelector, idSelector], (loaded, id) =>
    loaded.hasOwnProperty(id)
);

export const getTotalBidLimitBalance = (state: GlobalState, catalogId: number) => {
    const byId = byIdSelector(state);
    const catalogRegistration = getCatalogRegistration(state, catalogId);
    if (!catalogRegistration.limited) {
        return Infinity;
    }

    const data = byId[catalogId] || { balance: 0 };
    return data.balance || 0;
};

export const getBidLimitBalance = (state: GlobalState, catalogId: number) => {
    const byId = byIdSelector(state);
    const catalogRegistration = getCatalogRegistration(state, catalogId);
    if (!catalogRegistration.limited) {
        return Infinity;
    }

    const balanceInfo = byId[catalogId];
    if (!Boolean(balanceInfo)) {
        return 0;
    }

    return Math.max(balanceInfo.balance, 0);
};

const getLoadTime = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

export const shouldFetchBidLimit = (state: GlobalState, catalogId: number) => {
    const item = getBidLimitBalance(state, catalogId);
    const loaded = getLoadTime(state, catalogId);
    const loading = isLoading(state, catalogId);
    const authToken = getAuthToken(state);
    const bidderId = getBidderId(state);

    if (!authToken) {
        return false;
    }
    if (bidderId === -1) {
        return false;
    }
    if (!catalogId) {
        return false;
    }
    if (!isEmpty(item) || item > 0) {
        const time = Date.now();
        const diff = time - loaded;
        if (diff < BID_LIMIT_SLICE_CACHE_TIME) {
            return false;
        }
    }
    return !loading;
};
